import React, { useEffect, useRef, useState } from 'react';
import { fosterSetup, type FosterHome, type FosterSetup, type FosterSetupState } from '../../lib/fosterAdapters';
import type { FosterSource } from '../../analytics';
import { formatPhoneNumber } from '../../lib/utils';
import Modal, { ModalTitle, ModalActions } from '../Modal/Modal';
import FosterContactFields, {
  EMPTY_FOSTER_CONTACT,
  isDraftSubmittable,
  type FosterContactDraft,
} from '../FosterContactFields/FosterContactFields';
import Button from '../Button/Button';
import NoticeIcon from '../NoticeIcon/NoticeIcon';
import SearchBar from '../SearchBar/SearchBar';
import StatusBadge from '../StatusBadge/StatusBadge';
import useExistingFosterLookup from '../../hooks/useExistingFosterLookup';
import styles from './TransferToFosterModal.module.scss';

/** What the caller needs to create the placement; the pet/device it belongs to is theirs to hold. */
export interface FosterTransferInput {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  saveToRoster: boolean;
  /** Which half of the picker produced this foster. Carried for analytics only. */
  source: FosterSource;
}

interface TransferToFosterModalProps {
  open: boolean;
  onClose: () => void;
  /** Device identifier shown in the copy. Null renders nothing, as `device` did. */
  deviceId: string | null;
  petName: string | null;
  /** Saved homes, so a repeat placement doesn't retype contact details. */
  homes: FosterHome[];
  /**
   * Whether `homes` has loaded. Required, not optional: the roster is fetched lazily — the Dashboard
   * starts it when this modal opens — so an empty `homes` means "not yet" as often as it means "no
   * saved homes", and telling a rescue it has none is what sends them to retype a contact.
   */
  homesLoaded: boolean;
  onTransfer: (transfer: FosterTransferInput) => void;
}

/** Pickable only with what the transfer mutation requires. */
function isAssignable(home: FosterHome): boolean {
  return !!home.email && !!home.phone;
}

/**
 * Why the picker has nothing to show, which is three different reasons wearing one empty state.
 *
 * "No saved fosters yet. Use New foster" is the only one that tells the rescue to type a contact, so
 * it has to be the one case where that's true. It was also being shown mid-load and to a rescue
 * whose homes all fail `isAssignable` — both of which push exactly the retyped near-duplicate the
 * picker exists to prevent.
 */
function pickerEmptyCopy(loaded: boolean, homeCount: number, assignableCount: number, search: string): string {
  if (!loaded) {
    return 'Loading your saved fosters…';
  }
  if (search.trim() && assignableCount > 0) {
    return `No saved fosters matching "${search.trim()}"`;
  }
  if (homeCount === 0) {
    return 'No saved fosters yet. Use New foster to add one.';
  }
  if (assignableCount === 0) {
    return 'None of your saved fosters have both an email and a phone number yet, which a placement needs. Add the missing details from the Fosters tab, or use New foster.';
  }
  return `No saved fosters matching "${search.trim()}"`;
}

function matchesQuery(home: FosterHome, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) {
    return true;
  }
  return [home.fullName, home.email, home.phone].some((field) => !!field && field.toLowerCase().includes(q));
}

/** Pets the household holds right now, for the confirm step's Household row. */
function occupancyLabel(count: number): string {
  if (count === 0) {
    return 'no pets';
  }
  return count === 1 ? '1 pet' : `${count} pets`;
}

/** Shared with the recall modal, which phrases the other side of the same fact. */
function readinessOf(home: FosterHome): FosterSetup {
  return fosterSetup({ hasSafeZone: home.hasSafeZone, hasWifi: home.hasWifiSetup });
}

function readinessBadge(
  state: FosterSetupState,
  missing: string | null,
): { variant: 'success' | 'warning' | 'error'; label: string } {
  if (state === 'ready') {
    return { variant: 'success', label: 'Safe Zone + WiFi ready' };
  }
  if (state === 'partial') {
    return { variant: 'warning', label: `${missing} needed` };
  }
  return { variant: 'error', label: 'Setup needed' };
}

/**
 * What the rescue is signing up for. they/them — a stored first name doesn't tell us the volunteer's
 * pronouns.
 */
function readinessCopy(home: FosterHome, petLabel: string): { state: FosterSetupState; lead: string; rest: string } {
  const { state, missing } = readinessOf(home);
  const history =
    home.placementCount > 0
      ? `${home.firstName} keeps the household they've used for their last ${home.placementCount} ${
          home.placementCount === 1 ? 'placement' : 'placements'
        }`
      : `${home.firstName} keeps the household already on file`;

  if (state === 'ready') {
    return {
      state,
      lead: 'Safe Zone + WiFi ready.',
      rest: `${history} — they won't be asked to set anything up. ${petLabel} just shows up there.`,
    };
  }
  if (state === 'partial') {
    return {
      state,
      lead: `${missing} needed.`,
      rest: `${history} — they only need to set up their ${missing} when ${petLabel} arrives.`,
    };
  }
  return {
    state,
    lead: 'Setup needed.',
    rest: `${home.firstName} will be asked to set up their Safe Zone and WiFi when ${petLabel} arrives.`,
  };
}

function placementLabel(home: FosterHome): string {
  const placements = `${home.placementCount} ${home.placementCount === 1 ? 'placement' : 'placements'}`;
  return home.activePlacementCount > 0 ? `${placements} · fostering now` : placements;
}

/**
 * Assigns a device's pet to a foster, picked off the roster or entered fresh.
 *
 * Both paths submit the same contact fields: `rescueCreatePetFosterTransfer` is reuse-first, so a
 * matching contact lands back in its configured household. Picking a saved foster is that same call
 * with the stored details filled in, which is what stops a retyped near-miss building a duplicate.
 */
export default function TransferToFosterModal({
  open,
  onClose,
  deviceId,
  petName,
  homes,
  homesLoaded,
  onTransfer,
}: TransferToFosterModalProps) {
  const [source, setSource] = useState<FosterSource>('saved');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [contact, setContact] = useState<FosterContactDraft>(EMPTY_FOSTER_CONTACT);
  const [saveToRoster, setSaveToRoster] = useState(true);
  const [confirming, setConfirming] = useState(false);
  /** Set once the rescue picks a tab, so a late-arriving roster can't move them off it. */
  const touchedSource = useRef(false);

  /**
   * A typed contact can still match a kept home, where the backend reuses it and ignores
   * `saveToRoster` — so the notice below swaps out the checkbox rather than leave a control that
   * does nothing.
   */
  const { existingName, check: checkForExisting, clear: clearExisting } = useExistingFosterLookup(contact);

  /**
   * Typing a contact counts as choosing "New foster", so the auto-switch below can't pull the rescue
   * off a form they have already started. Needed because the roster arrives late by design — the
   * Dashboard only fetches it once a modal that needs it is open, so `hasRoster` flips true a beat
   * after every opening.
   */
  function handleContactChange(next: FosterContactDraft) {
    touchedSource.current = true;
    setContact(next);
  }

  const assignable = homes.filter(isAssignable);
  const hasRoster = assignable.length > 0;

  // Reset per opening. Start on New when there's no roster to pick from.
  useEffect(() => {
    if (!open) {
      return;
    }
    setSource(assignable.length > 0 ? 'saved' : 'new');
    setSelectedId(null);
    setSearch('');
    setContact(EMPTY_FOSTER_CONTACT);
    setSaveToRoster(true);
    clearExisting();
    setConfirming(false);
    touchedSource.current = false;
    // Open-only: `assignable.length` just seeds the initial tab.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // The roster can arrive after the modal opens — it's skipped on the Adoptions tab, and the query
  // is unpaginated. Without this the modal stays on "New foster" and the rescue retypes a contact
  // that the picker would have offered, which is the near-miss duplicate the picker exists to stop.
  useEffect(() => {
    if (open && hasRoster && !touchedSource.current) {
      setSource('saved');
    }
  }, [open, hasRoster]);

  if (!deviceId) {
    return null;
  }
  const visible = assignable.filter((home) => matchesQuery(home, search));
  const selected = assignable.find((home) => home.id === selectedId) ?? null;

  // Shared with AddFosterModal: both create the same contact, so neither should accept what the
  // other rejects and leave the rescue to discover it from a server error.
  const canSubmit = source === 'saved' ? !!selected : isDraftSubmittable(contact);

  function handleClose() {
    onClose();
  }

  function handleConfirm() {
    if (!canSubmit) {
      return;
    }
    if (source === 'saved') {
      if (!selected) {
        return;
      }
      // Show what the rescue is committing to before it happens.
      if (!confirming) {
        setConfirming(true);
        return;
      }
      onTransfer({
        firstName: selected.firstName,
        lastName: selected.lastName ?? '',
        email: selected.email ?? '',
        phone: selected.phone ?? '',
        // A saved home is on the roster already, and the backend ignores the flag for one.
        saveToRoster: true,
        source: 'saved',
      });
    } else {
      onTransfer({
        firstName: contact.firstName.trim(),
        lastName: contact.lastName.trim(),
        email: contact.email.trim(),
        phone: contact.phone.trim(),
        saveToRoster,
        source: 'new',
      });
    }
    handleClose();
  }

  const petLabel = petName || 'the pet';

  if (confirming && selected) {
    const { state, lead, rest } = readinessCopy(selected, petLabel);
    const ready = state === 'ready';
    // Same colors as the picker chip that led here.
    const noticeTone =
      state === 'ready' ? styles.noticeSuccess : state === 'partial' ? styles.noticeWarning : styles.noticeError;
    return (
      <Modal open={open} onClose={handleClose}>
        <ModalTitle>Assign to foster</ModalTitle>
        <p className={styles.modalDescription}>
          Transfer <strong>{deviceId}</strong>
          {petName ? ` (${petName})` : ''} to <strong>{selected.fullName}</strong>.
        </p>

        <div className={`${styles.notice} ${noticeTone} ${styles.noticeSpaced}`}>
          <NoticeIcon variant={ready ? 'success' : 'caution'} />
          <div>
            <b>{lead}</b> {rest}
          </div>
        </div>

        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>Foster</span>
          <span className={styles.detailValue}>{selected.fullName}</span>
        </div>
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>Email</span>
          <span className={styles.detailValue}>{selected.email ?? '—'}</span>
        </div>
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>Phone</span>
          <span className={styles.detailValue}>{selected.phone ? formatPhoneNumber(selected.phone) : '—'}</span>
        </div>
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>Household</span>
          <span className={styles.detailValue}>
            On file · Foster household · {occupancyLabel(selected.placedPetCount)}
          </span>
        </div>
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>Placements</span>
          <span className={styles.detailValue}>{selected.placementCount}</span>
        </div>

        <ModalActions>
          <Button variant="ghost" onClick={() => setConfirming(false)}>
            Back
          </Button>
          <Button variant="green" onClick={handleConfirm}>
            Assign to Foster
          </Button>
        </ModalActions>
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalTitle>Assign to foster</ModalTitle>
      <p className={styles.modalDescription}>
        Transfer <strong>{deviceId}</strong>
        {petName ? ` (${petName})` : ''} to a foster.
      </p>

      <div className={styles.seg}>
        <button
          type="button"
          className={`${styles.segOpt}${source === 'saved' ? ` ${styles.segOptActive}` : ''}`}
          aria-pressed={source === 'saved'}
          onClick={() => {
            touchedSource.current = true;
            setSource('saved');
          }}
        >
          <svg
            className={styles.segIcon}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M17 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
            <circle cx="9.5" cy="7" r="4" />
            <polyline points="17 11 19 13 23 9" />
          </svg>
          Saved foster
        </button>
        <button
          type="button"
          className={`${styles.segOpt}${source === 'new' ? ` ${styles.segOptActive}` : ''}`}
          aria-pressed={source === 'new'}
          onClick={() => {
            touchedSource.current = true;
            setSource('new');
          }}
        >
          <svg
            className={styles.segIcon}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <line x1="19" y1="8" x2="19" y2="14" />
            <line x1="22" y1="11" x2="16" y2="11" />
          </svg>
          New foster
        </button>
      </div>

      {source === 'saved' ? (
        <>
          <SearchBar value={search} onChange={setSearch} placeholder="Search your roster…" />
          <div className={styles.picker}>
            {visible.length === 0 ? (
              <div className={styles.pickerEmpty}>
                {pickerEmptyCopy(homesLoaded, homes.length, assignable.length, search)}
              </div>
            ) : (
              visible.map((home) => {
                const { state, missing } = readinessOf(home);
                const badge = readinessBadge(state, missing);
                return (
                  <label
                    key={home.id}
                    className={`${styles.pickRow}${selectedId === home.id ? ` ${styles.pickRowSelected}` : ''}`}
                  >
                    <input
                      type="radio"
                      name="assign-foster-pick"
                      className={styles.pickRadio}
                      checked={selectedId === home.id}
                      onChange={() => setSelectedId(home.id)}
                    />
                    <div className={styles.pickInfo}>
                      <div className={styles.pickName}>{home.fullName}</div>
                      <div className={styles.pickContact}>
                        {[home.email, home.phone ? formatPhoneNumber(home.phone) : null].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                    <div className={styles.pickRight}>
                      <StatusBadge variant={badge.variant}>{badge.label}</StatusBadge>
                      <div className={styles.pickCount}>{placementLabel(home)}</div>
                    </div>
                  </label>
                );
              })
            )}
          </div>
        </>
      ) : (
        <>
          <FosterContactFields value={contact} onChange={handleContactChange} onContactBlur={checkForExisting} />

          {existingName ? (
            <div className={`${styles.notice} ${styles.noticeCaution}`}>
              <NoticeIcon variant="caution" />
              <div>
                <strong>{existingName}</strong> is already on your roster, so this placement goes to their existing
                household and stays saved.
              </div>
            </div>
          ) : (
            <label className={styles.check}>
              <input
                type="checkbox"
                className={styles.checkBox}
                checked={saveToRoster}
                onChange={(e) => setSaveToRoster(e.target.checked)}
              />
              <div>
                <div className={styles.checkTitle}>Save to foster roster</div>
                <div className={styles.checkHint}>
                  Keeps their contact info and household so the next placement is one click.
                </div>
              </div>
            </label>
          )}
        </>
      )}

      <ModalActions>
        <Button variant="ghost" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="green" onClick={handleConfirm} disabled={!canSubmit}>
          Assign to Foster
        </Button>
      </ModalActions>
    </Modal>
  );
}
